import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  createRoute,
  deleteRoute,
  getReverseRoutes,
  getRouteConfiguration,
} from 'api/routes/routesApi';
import { providerRoutesQuery } from 'api/routes/providerRoutesQuery';
import { SessionExpiredError } from 'api/apiUtils';
import { queryKeys } from 'api/queryKeys';
import { Destination } from 'components/common/ObjectTypes';
import { FieldMappingsConfig } from '../transformationRules/fieldMappingTypes';
import { reverseRouteName } from '../hideReverseRoutes';
import { routeRecordId } from '../routeDisplayName';
import type { RouteRecord } from './useRouteRecord';

export type ReverseConfigBySite = Record<
  string,
  { routeId: string; name: string; data: FieldMappingsConfig }
>;
type ReverseData = {
  map: Record<string, string[]>;
  shared: { id: string; name: string }[];
  configs: ReverseConfigBySite;
};

// Stable empties for data that hasn't loaded yet, so memos and effects that
// depend on them don't re-run on every render.
const EMPTY_REVERSE_MAP: Record<string, string[]> = {};
const EMPTY_SHARED: { id: string; name: string }[] = [];
const EMPTY_REVERSE_CONFIGS: ReverseConfigBySite = {};

// The other Routes around this one: the reverse Route of each site (data
// coming back to our provider), unrelated Routes sharing our provider's
// account, and how many other Routes our provider feeds.
export const useRelatedRoutes = (record: RouteRecord) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { getToken, route, routeProvider, defaultRouteId } = record;

  const tokenUsable = !!getToken();

  // The other Routes this same provider integration feeds. `enabled` is a field
  // on that integration, so disabling from one Route's map stops all of them —
  // this is what the confirmation has to name. Distinct from
  // providerSharedWith, which is the opposite direction (Routes sending INTO
  // our provider) and shares action configurations rather than enabled state.
  // The count is the API's total, not the fetched page's length: the request is
  // capped at page_size, and a provider feeding 40 Routes must not be described
  // as feeding 19. `null` means the lookup failed — "no other Routes" is unknown,
  // so the confirmation takes its cautious wording instead.
  const providerRouteCountQuery = useQuery({
    ...providerRoutesQuery(getToken() ?? '', routeProvider?.id ?? ''),
    enabled: !!routeProvider?.id && tokenUsable,
  });
  const providerAlsoProvidesFor: number | null = (() => {
    const data = providerRouteCountQuery.data;
    if (!data) return 0;
    if (!data.ok) return null;
    // Every Route of this provider except the one on screen. The one on
    // screen is by definition one of them, so it always comes off the
    // total — checking whether it appears in the fetched page would
    // over-count by one for a provider with more Routes than fit a page.
    const total = data.count ?? data.results.length;
    return Math.max(0, total - (defaultRouteId ? 1 : 0));
  })();

  // siteId -> route ids carrying data from that site back to our provider, the
  // other Routes that send data to our provider (action configurations live on
  // the integration record, so edits here are inherited by those flows too),
  // and each reverse Route's field mappings.
  const reverseSiteIds = useMemo(
    () => route.destinations.map((d) => d.id).sort(),
    [route.destinations],
  );
  const reverseQueryKey = [
    ...queryKeys.routes.reverse(routeProvider?.id ?? ''),
    reverseSiteIds,
  ];
  const reverseQuery = useQuery({
    queryKey: reverseQueryKey,
    enabled: !!routeProvider?.id && tokenUsable,
    queryFn: async (): Promise<ReverseData> => {
      const token = getToken();
      if (!token) throw new SessionExpiredError();
      const providerId = routeProvider.id;
      // Routes whose destination is OUR provider = candidate reverse flows.
      const data = await getReverseRoutes(token, providerId);
      const siteIds = new Set(reverseSiteIds);
      const map: Record<string, string[]> = {};
      const shared: { id: string; name: string }[] = [];
      // ?destination_id= selects Routes, not routing rules: a sender that also
      // routes elsewhere returns ALL of its routing rules. Deleting those to
      // turn one direction off would destroy unrelated flows, so keep only the
      // rules that actually route to this Route's provider.
      const senders = (data?.results || data || []).filter(
        (c: any) => c?.provider?.id,
      );
      const routesToProvider = async (rules: any[]) => {
        const kept: string[] = [];
        for (const rule of rules) {
          if (!rule?.id) continue;
          const declared = rule.destinations;
          if (Array.isArray(declared)) {
            if (declared.some((d: any) => (d?.id || d) === providerId))
              kept.push(rule.id);
            continue;
          }
          // The rule came back without its destinations — ask the Route.
          try {
            const ruleRoute = await getRouteConfiguration(token, rule.id);
            const dests = ruleRoute?.destinations || [];
            if (dests.some((d: any) => (d?.id || d) === providerId))
              kept.push(rule.id);
          } catch (e) {
            console.log('could not resolve route destinations', rule.id, e);
          }
        }
        return kept;
      };
      for (const c of senders) {
        if (siteIds.has(c.provider.id)) {
          map[c.provider.id] = await routesToProvider(c.routing_rules || []);
        } else {
          // Anything else is an unrelated Route sharing this account.
          // Link by the provider INTEGRATION id — that's what route
          // URLs address. Still require a route record: a provider
          // with none has no Route page to link to.
          if (routeRecordId(c) && c.id)
            shared.push({ id: c.id, name: c.provider.name });
        }
      }
      // The reverse flows' field mappings live on those route records, so
      // fetch each one's configuration — the "in" arrow has nothing to
      // show (or to edit) without it. First route per site: that is the
      // one the enable/disable toggle created and addresses.
      const configs: ReverseConfigBySite = {};
      // Concurrently: one Route can have many bidirectional sites, and
      // serial fetches add a round-trip of latency each before any
      // incoming arrow can show its rules.
      await Promise.all(
        Object.entries(map).map(async ([siteId, ids]) => {
          const reverseId = ids[0];
          if (!reverseId) return;
          try {
            const reverseRoute = await getRouteConfiguration(token, reverseId);
            configs[siteId] = {
              routeId: reverseId,
              name: reverseRoute?.configuration?.name ?? '',
              data: reverseRoute?.configuration?.data ?? { field_mappings: {} },
            };
          } catch (e) {
            console.log(
              'could not load reverse route configuration',
              reverseId,
              e,
            );
          }
        }),
      );
      return { map, shared, configs };
    },
  });
  const reverseRoutesBySite: Record<string, string[]> =
    reverseQuery.data?.map ?? EMPTY_REVERSE_MAP;
  const providerSharedWith: { id: string; name: string }[] =
    reverseQuery.data?.shared ?? EMPTY_SHARED;
  const reverseConfigBySite: ReverseConfigBySite =
    reverseQuery.data?.configs ?? EMPTY_REVERSE_CONFIGS;
  const refreshReverseRoutes = () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.routes.reverse(routeProvider.id),
    });

  // A saved or deleted rule on a reverse Route lands in the cached copy.
  const storeReverseConfig = (
    siteId: string,
    data: FieldMappingsConfig,
    name?: string,
  ) =>
    queryClient.setQueryData(
      reverseQueryKey,
      (prev: ReverseData | undefined) => {
        const current = prev?.configs[siteId];
        if (!prev || !current) return prev;
        return {
          ...prev,
          configs: {
            ...prev.configs,
            [siteId]: { ...current, data, name: name ?? current.name },
          },
        };
      },
    );

  // ── Bidirectional flows (GUNDI-5595) ────────────────────────────────────
  // The reverse direction is a second route: this Route's destination
  // becomes the data provider, and this Route's provider becomes its
  // destination. Both routes reuse the SAME integration records, and the UI
  // presents them as one bidirectional flow.
  const handleEnableReverseFlow = async (dest: Destination) => {
    const token = getToken();
    if (!token) return;
    const ownerId = route.owner?.id || routeProvider.owner?.id;
    if (!ownerId) return;
    try {
      await createRoute(token, {
        // The list relies on this exact shape to spot the reverse Route (hideReverseRoutes).
        name: reverseRouteName(dest.name, routeProvider.name),
        owner: ownerId,
        data_providers: [dest.id],
        destinations: [routeProvider.id],
      });
      toast.success(`${t('Route updated')}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      refreshReverseRoutes();
    } catch (err: any) {
      console.log(err);
      if (err?.response?.status === 401) return;
      toast.error(
        `${t('Something went wrong. Please try again or contact support.')}`,
        { position: toast.POSITION.TOP_RIGHT },
      );
    }
  };

  const handleDisableReverseFlow = async (dest: Destination) => {
    const token = getToken();
    if (!token) return;
    const routeIds = reverseRoutesBySite[dest.id] || [];
    if (routeIds.length === 0) {
      // The reverse flow exists (that is why the toggle is on) but we could not
      // resolve which route carries it. Deleting a guess could take out unrelated
      // Routes, so say so instead.
      console.log('no resolved reverse route to delete for', dest.id);
      toast.error(
        `${t('Something went wrong. Please try again or contact support.')}`,
        { position: toast.POSITION.TOP_RIGHT },
      );
      return;
    }
    try {
      for (const routeId of routeIds) await deleteRoute(token, routeId);
      toast.success(`${t('Route updated')}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      refreshReverseRoutes();
    } catch (err: any) {
      console.log(err);
      if (err?.response?.status === 401) return;
      toast.error(
        `${t('Something went wrong. Please try again or contact support.')}`,
        { position: toast.POSITION.TOP_RIGHT },
      );
    }
  };

  return {
    providerAlsoProvidesFor,
    reverseRoutesBySite,
    providerSharedWith,
    reverseConfigBySite,
    refreshReverseRoutes,
    storeReverseConfig,
    handleEnableReverseFlow,
    handleDisableReverseFlow,
  };
};
